import { useState, useEffect } from 'react'
import {
  X, User, Lock, LogOut, Eye, EyeOff, CheckCircle,
  AlertCircle, Scissors, Clock, Key, Building2
} from 'lucide-react'
import type { AccessCode } from '../types'
import { changePassword } from '../supabase'

interface Props {
  open: boolean
  onClose: () => void
  onLogout: () => void
  authCode: string
  salonInfo: AccessCode | null
}

// ── Formatage de date ──────────────────────────────────────────────────────────
function fmtDate(iso?: string | null) {
  if (!iso) return '—'
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  }).format(new Date(iso))
}

// ── Formulaire changement de mot de passe ─────────────────────────────────────
function ChangerMdp({ authCode, onSuccess }: { authCode: string; onSuccess: () => void }) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNext, setShowNext] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)

  const valid = next.length >= 8 && next === confirm
  const mismatch = confirm.length > 0 && next !== confirm

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    if (!valid) return
    setLoading(true)
    const res = await changePassword(authCode, current, next)
    setLoading(false)
    if (res.success) {
      setSuccess(true)
      setCurrent(''); setNext(''); setConfirm('')
      setTimeout(onSuccess, 1800)
    } else {
      setError(res.error ?? 'Erreur inconnue.')
    }
  }

  if (success) {
    return (
      <div className="flex flex-col items-center gap-2 py-4 text-center">
        <CheckCircle className="w-8 h-8" style={{ color: '#16A34A' }} />
        <div className="font-semibold text-green-700">Mot de passe modifié !</div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {/* Actuel */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Mot de passe actuel</label>
        <div className="relative">
          <input
            type={showCurrent ? 'text' : 'password'}
            value={current}
            onChange={e => setCurrent(e.target.value)}
            placeholder="Mot de passe actuel"
            className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          <button type="button" onClick={() => setShowCurrent(!showCurrent)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
      </div>

      {/* Nouveau */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Nouveau mot de passe</label>
        <div className="relative">
          <input
            type={showNext ? 'text' : 'password'}
            value={next}
            onChange={e => setNext(e.target.value)}
            placeholder="Min. 8 caractères"
            className="w-full px-3 py-2 pr-10 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300"
          />
          <button type="button" onClick={() => setShowNext(!showNext)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            {showNext ? <EyeOff size={15} /> : <Eye size={15} />}
          </button>
        </div>
        {/* Barre de force */}
        {next.length > 0 && (
          <div className="mt-1.5 flex gap-1">
            {[1, 2, 3, 4].map(i => {
              const strength = next.length >= 12 ? 4 : next.length >= 10 ? 3 : next.length >= 8 ? 2 : 1
              const color = strength >= 4 ? '#16A34A' : strength >= 3 ? '#16A34A' : strength >= 2 ? '#E65100' : '#C62828'
              return (
                <div key={i} className="flex-1 h-1 rounded-full transition-all"
                  style={{ background: i <= strength ? color : '#E2E8F0' }} />
              )
            })}
            <span className="text-xs ml-1" style={{
              color: next.length >= 12 ? '#16A34A' : next.length >= 8 ? '#E65100' : '#C62828'
            }}>
              {next.length >= 12 ? 'Fort' : next.length >= 8 ? 'Moyen' : 'Trop court'}
            </span>
          </div>
        )}
      </div>

      {/* Confirmation */}
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Confirmer le nouveau mot de passe</label>
        <input
          type="password"
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
          placeholder="Répétez le mot de passe"
          className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-300 transition-colors"
          style={{ borderColor: mismatch ? '#C62828' : '' }}
        />
        {mismatch && (
          <p className="text-xs mt-0.5" style={{ color: '#C62828' }}>Les mots de passe ne correspondent pas.</p>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-xl text-sm"
          style={{ background: '#FFF5F5', color: '#C62828', border: '1px solid #FEE2E2' }}>
          <AlertCircle size={15} />
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || !valid || !current}
        className="w-full py-2.5 rounded-xl text-sm font-semibold text-white transition-all disabled:opacity-40"
        style={{ background: '#1A3C6E' }}>
        {loading ? 'Modification...' : 'Modifier le mot de passe'}
      </button>
    </form>
  )
}

// ── Composant principal ────────────────────────────────────────────────────────
export default function MonComptePanel({ open, onClose, onLogout, authCode, salonInfo }: Props) {
  const [tab, setTab] = useState<'infos' | 'mdp'>('infos')
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)

  // Fermer avec Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    if (open) document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  // Bloquer le scroll du body
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [open])

  if (!open) return null

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Panneau latéral droit */}
      <div
        className="fixed top-0 right-0 z-50 h-full w-full max-w-sm bg-white shadow-2xl flex flex-col"
        style={{ animation: 'slideInRight 0.25s ease-out' }}>

        {/* ── Header du panel ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100"
          style={{ background: '#1A3C6E' }}>
          <div className="flex items-center gap-3">
            {/* Avatar initiales */}
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-blue-900"
              style={{ background: 'white' }}>
              {salonInfo
                ? `${salonInfo.prenom.charAt(0)}${salonInfo.nom.charAt(0)}`.toUpperCase()
                : <User size={18} />
              }
            </div>
            <div>
              <div className="font-bold text-sm text-white">
                {salonInfo ? `${salonInfo.prenom} ${salonInfo.nom}` : 'Mon compte'}
              </div>
              <div className="text-xs text-white/60">
                {salonInfo?.salon ?? 'Chargement...'}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/10">
            <X size={18} />
          </button>
        </div>

        {/* ── Onglets ── */}
        <div className="flex border-b border-gray-100">
          {([
            { id: 'infos', label: 'Mon profil', icon: User },
            { id: 'mdp',   label: 'Mot de passe', icon: Lock },
          ] as const).map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setTab(id)}
              className="flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-medium transition-colors"
              style={{
                color: tab === id ? '#1A3C6E' : '#9CA3AF',
                borderBottom: tab === id ? '2px solid #1A3C6E' : '2px solid transparent'
              }}>
              <Icon size={13} />
              {label}
            </button>
          ))}
        </div>

        {/* ── Corps scrollable ── */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* ═══ ONGLET PROFIL ═══ */}
          {tab === 'infos' && (
            <>
              {/* Bloc salon */}
              <div className="rounded-2xl overflow-hidden border border-gray-100">
                <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 flex items-center gap-2">
                  <Building2 size={12} /> Votre salon
                </div>
                <div className="divide-y divide-gray-50">
                  {[
                    { label: 'Nom du salon',  value: salonInfo?.salon    ?? '—' },
                    { label: 'Adresse',       value: salonInfo?.adresse  ?? '—' },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between items-start px-4 py-3">
                      <span className="text-xs text-gray-500">{label}</span>
                      <span className="text-sm font-medium text-gray-800 text-right max-w-[55%]">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bloc identité */}
              <div className="rounded-2xl overflow-hidden border border-gray-100">
                <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 flex items-center gap-2">
                  <User size={12} /> Informations personnelles
                </div>
                <div className="divide-y divide-gray-50">
                  {[
                    { label: 'Prénom',    value: salonInfo?.prenom    ?? '—' },
                    { label: 'Nom',       value: salonInfo?.nom       ?? '—' },
                    { label: 'Email',     value: salonInfo?.email     ?? '—' },
                    { label: 'Téléphone',value: salonInfo?.telephone ?? '—' },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between items-center px-4 py-3">
                      <span className="text-xs text-gray-500">{label}</span>
                      <span className="text-sm font-medium text-gray-800">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Bloc accès */}
              <div className="rounded-2xl overflow-hidden border border-gray-100">
                <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wide bg-gray-50 flex items-center gap-2">
                  <Key size={12} /> Accès & sécurité
                </div>
                <div className="divide-y divide-gray-50">
                  <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-xs text-gray-500">Code d'accès</span>
                    <span className="text-sm font-mono font-bold" style={{ color: '#1A3C6E' }}>{authCode}</span>
                  </div>
                  <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-xs text-gray-500 flex items-center gap-1"><Clock size={10} /> Première connexion</span>
                    <span className="text-xs text-gray-700">{fmtDate(salonInfo?.first_login_at)}</span>
                  </div>
                  <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-xs text-gray-500 flex items-center gap-1"><Clock size={10} /> Dernière connexion</span>
                    <span className="text-xs text-gray-700">{fmtDate(salonInfo?.last_login_at)}</span>
                  </div>
                  <div className="flex justify-between items-center px-4 py-3">
                    <span className="text-xs text-gray-500">Statut</span>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{
                        background: salonInfo?.is_active ? '#F0FDF4' : '#FFF5F5',
                        color: salonInfo?.is_active ? '#16A34A' : '#C62828'
                      }}>
                      {salonInfo?.is_active ? '✓ Actif' : '✗ Inactif'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Info outil */}
              <div className="rounded-xl px-4 py-3 text-xs text-gray-500 flex items-center gap-2"
                style={{ background: '#F0F7FF', border: '1px solid #DBEAFE' }}>
                <Scissors size={13} style={{ color: '#1A3C6E', flexShrink: 0 }} />
                <span>
                  Outil <strong style={{ color: '#1A3C6E' }}>Loris Hug Innovation</strong> — Pilotage Salon.
                  Vos données sont sauvegardées automatiquement.
                </span>
              </div>
            </>
          )}

          {/* ═══ ONGLET MOT DE PASSE ═══ */}
          {tab === 'mdp' && (
            <>
              <div className="rounded-xl px-4 py-3 text-xs text-gray-500 flex items-start gap-2"
                style={{ background: '#FFFBEB', border: '1px solid #FDE68A' }}>
                <Lock size={13} style={{ color: '#92400E', flexShrink: 0, marginTop: 1 }} />
                <span>
                  Votre mot de passe est chiffré (SHA-256). Loris Hug Innovation n'y a pas accès.
                </span>
              </div>
              <ChangerMdp authCode={authCode} onSuccess={() => setTab('infos')} />
            </>
          )}
        </div>

        {/* ── Footer : déconnexion ── */}
        <div className="border-t border-gray-100 p-4">
          {showLogoutConfirm ? (
            <div className="space-y-2">
              <p className="text-xs text-center text-gray-500">Confirmer la déconnexion ?</p>
              <div className="flex gap-2">
                <button onClick={() => setShowLogoutConfirm(false)}
                  className="flex-1 py-2 text-sm border border-gray-200 rounded-xl text-gray-500 hover:bg-gray-50 transition-colors">
                  Annuler
                </button>
                <button onClick={onLogout}
                  className="flex-1 py-2 text-sm font-semibold rounded-xl text-white transition-colors"
                  style={{ background: '#C62828' }}>
                  Se déconnecter
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowLogoutConfirm(true)}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border border-red-100 text-red-500 hover:bg-red-50 transition-colors">
              <LogOut size={15} />
              Se déconnecter
            </button>
          )}
        </div>
      </div>

      {/* Animation CSS */}
      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);   opacity: 1; }
        }
      `}</style>
    </>
  )
}
